import React, { useEffect } from 'react';
import { Box, Typography, Breadcrumbs, Link, Grid, Card, CardContent, Chip, Divider, Stack, Paper } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getPromotion, clearPromotionData, getCitiesList, getInitiatorsList } from '../../../../store/companyPromotions';
import { formatDate } from '../../../../utils/formatDate';
import { StatusColor } from '../../../../components/StatusColor';
import { CircularProgressCenter } from '../../../../components/CircularProgressCenter';

export const DetailCompanyPromotion = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  
  const {
    currentPromotion,
    promotionLoading,
    promotionError,
    cities,
    initiators
  } = useSelector((state) => state.companyPromotions);

  // Функция для получения перевода по языку
  const getTranslation = (lang) => {
    return currentPromotion?.translations?.find(t => t.lang === lang) || {};
  };

  // Функция для получения филиалов по ID
  const getFilialsByIds = (filialIds) => {
    if (!filialIds || !cities?.filials) return [];
    return cities.filials.filter(filial => filialIds.includes(filial.filial_id));
  };

  // Функция для получения инициатора по ID
  const getInitiatorById = (initiatorId) => {
    if (!initiatorId || !initiators) return null;
    return initiators?.initiators.find(initiator => initiator.id === initiatorId);
  };

  useEffect(() => {
    if (id) {
      dispatch(getPromotion(id));
    }
    dispatch(getCitiesList());
    dispatch(getInitiatorsList());
    
    return () => {
      dispatch(clearPromotionData());
    };
  }, [dispatch, id]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'NEW':
        return '#1976d2'; // Синий
      case 'IN_APPROVAL':
        return '#ff9800'; // Оранжевый
      case 'REJECTED':
        return '#f44336'; // Красный
      case 'APPROVED':
        return '#4caf50'; // Зеленый
      case 'ACTIVE':
        return '#2e7d32'; // Темно-зеленый
      case 'DEACTIVATED':
        return '#757575'; // Серый
      default:
        return '#757575';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'NEW':
        return 'Новая';
      case 'IN_APPROVAL':
        return 'На рассмотрении';
      case 'REJECTED':
        return 'Отклонена';
      case 'APPROVED':
        return 'Одобрена';
      case 'ACTIVE':
        return 'Активна';
      case 'DEACTIVATED':
        return 'Деактивирована';
      default:
        return status;
    }
  };

  if (promotionLoading) {
    return <CircularProgressCenter />;
  }

  if (promotionError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="error">
          Ошибка: {promotionError}
        </Typography>
      </Box>
    );
  }

  if (!currentPromotion) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="text.secondary">
          Акция не найдена
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: '24px' }}>
        <Link href="/company-promotions" underline="hover">
          Акции компании
        </Link>
        <Typography color="text.primary">
          Детали акции
        </Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h1" sx={{ color: 'black' }} component="h1">
          Детали акции
        </Typography>
        <StatusColor 
          color={getStatusColor(currentPromotion.status)} 
          text={getStatusText(currentPromotion.status)} 
        />
      </Box>

      <Grid container spacing={3}>
        {/* Основная информация */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2, color: 'black' }}>
                Основная информация
              </Typography>
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    ID акции
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {currentPromotion.id}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Название
                  </Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">RU:</Typography>
                      <Typography variant="body1">{getTranslation('ru').promotion_name || 'Не указано'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">KK:</Typography>
                      <Typography variant="body1">{getTranslation('kk').promotion_name || 'Не указано'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">EN:</Typography>
                      <Typography variant="body1">{getTranslation('en').promotion_name || 'Не указано'}</Typography>
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Краткое описание
                  </Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">RU:</Typography>
                      <Typography variant="body1">{getTranslation('ru').short_description || 'Не указано'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">KK:</Typography>
                      <Typography variant="body1">{getTranslation('kk').short_description || 'Не указано'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">EN:</Typography>
                      <Typography variant="body1">{getTranslation('en').short_description || 'Не указано'}</Typography>
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Полное описание
                  </Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">RU:</Typography>
                      <Box 
                        sx={{ 
                          border: '1px solid #e0e0e0', 
                          borderRadius: 1, 
                          p: 2, 
                          minHeight: '100px',
                          backgroundColor: '#fafafa'
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: getTranslation('ru').description || 'Не указано' 
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">KK:</Typography>
                      <Box 
                        sx={{ 
                          border: '1px solid #e0e0e0', 
                          borderRadius: 1, 
                          p: 2, 
                          minHeight: '100px',
                          backgroundColor: '#fafafa'
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: getTranslation('kk').description || 'Не указано' 
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">EN:</Typography>
                      <Box 
                        sx={{ 
                          border: '1px solid #e0e0e0', 
                          borderRadius: 1, 
                          p: 2, 
                          minHeight: '100px',
                          backgroundColor: '#fafafa'
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: getTranslation('en').description || 'Не указано' 
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Сроки и настройки */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* Сроки */}
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ mb: 2, color: 'black' }}>
                  Сроки проведения
                </Typography>
                
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Дата начала
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currentPromotion.start_at ? formatDate(currentPromotion.start_at) : 'Не указано'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Дата окончания
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currentPromotion.end_at ? formatDate(currentPromotion.end_at) : 'Не указано'}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Настройки доступности */}
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ mb: 2, color: 'black' }}>
                  Настройки доступности
                </Typography>
                
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Филиалы
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {currentPromotion.filial_ids?.length > 0 ? (
                        getFilialsByIds(currentPromotion.filial_ids).map((filial, index) => (
                          <Chip key={index} label={filial.city_name} size="small" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Все филиалы
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Доступность
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currentPromotion.is_enabled_for_all_customers 
                        ? 'Всем клиентам' 
                        : 'По списку клиентов'}
                    </Typography>
                  </Box>

                  {currentPromotion.files?.length && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Файлы
                      </Typography>
                      {currentPromotion.files.map((file) => (
                        <Chip
                          key={file.id}
                          sx={{ fontWeight: 500 }}
                          label={file.filename || 'Не указано'}
                          clickable
                          onClick={() => window.open(file.url, '_blank')}
                        />
                      ))}
                    </Box>
                  )}

                  {currentPromotion.is_button_enabled && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Кнопки в карточке
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Включены
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Кнопки */}
        {currentPromotion.buttons && currentPromotion.buttons.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ mb: 2, color: 'black' }}>
                  Кнопки акции
                </Typography>
                
                <Stack spacing={2}>
                  {currentPromotion.buttons.map((button, index) => (
                    <Paper key={index} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="h6" sx={{ mb: 2, color: 'black' }}>
                        {button.button_name || `Кнопка ${index + 1}`}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Текст кнопки
                          </Typography>
                          <Stack spacing={0.5}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">RU:</Typography>
                              <Typography variant="body2">{button.text_ru || 'Не указано'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">KK:</Typography>
                              <Typography variant="body2">{button.text_kk || 'Не указано'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">EN:</Typography>
                              <Typography variant="body2">{button.text_en || 'Не указано'}</Typography>
                            </Box>
                          </Stack>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Тип ссылки
                          </Typography>
                          <Chip 
                            label={button.is_external_link ? 'Внешняя ссылка' : 'Внутренняя ссылка'} 
                            color={button.is_external_link ? 'warning' : 'primary'}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Ссылка
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              wordBreak: 'break-all',
                              color: button.is_external_link ? 'primary.main' : 'text.primary'
                            }}
                          >
                            {button.link || 'Не указано'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Баннеры */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2, color: 'black' }}>
                Баннеры
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Баннер (RU)
                  </Typography>
                  {getTranslation('ru').banner_url ? (
                    <Box 
                      component="img"
                      src={getTranslation('ru').banner_url}
                      alt="Баннер RU"
                      sx={{ 
                        width: '100%', 
                        height: 'auto', 
                        maxHeight: '200px',
                        objectFit: 'contain',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1
                      }}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        height: '100px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #ccc',
                        borderRadius: 1,
                        backgroundColor: '#fafafa'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Баннер не загружен
                      </Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Баннер (KK)
                  </Typography>
                  {getTranslation('kk').banner_url ? (
                    <Box 
                      component="img"
                      src={getTranslation('kk').banner_url}
                      alt="Баннер KK"
                      sx={{ 
                        width: '100%', 
                        height: 'auto', 
                        maxHeight: '200px',
                        objectFit: 'contain',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1
                      }}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        height: '100px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #ccc',
                        borderRadius: 1,
                        backgroundColor: '#fafafa'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Баннер не загружен
                      </Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Баннер (EN)
                  </Typography>
                  {getTranslation('en').banner_url ? (
                    <Box 
                      component="img"
                      src={getTranslation('en').banner_url}
                      alt="Баннер EN"
                      sx={{ 
                        width: '100%', 
                        height: 'auto', 
                        maxHeight: '200px',
                        objectFit: 'contain',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1
                      }}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        height: '100px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #ccc',
                        borderRadius: 1,
                        backgroundColor: '#fafafa'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Баннер не загружен
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Инициатор */}
        {currentPromotion.initiator_id && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ mb: 2, color: 'black' }}>
                  Инициатор акции
                </Typography>
                
                {(() => {
                  const initiator = getInitiatorById(currentPromotion.initiator_id);
                  return initiator ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Имя
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {initiator.name || 'Не указано'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Фамилия
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {initiator.surname || 'Не указано'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Отдел
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {initiator.department || 'Не указано'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Телефон
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {initiator.phone || 'Не указано'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Информация об инициаторе загружается...
                    </Typography>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
